export const CHARACTER_IDS = [
  'normal',
  'gamer',
  'nerd',
  'goth',
  'punk',
  'old-guy',
  'alien',
  'robot',
  'skeleton',
  'gopher',
  'cat',
  'hot-dog',
] as const;

export type CharacterId = (typeof CHARACTER_IDS)[number];

export const CHARACTER_META: Record<
  CharacterId,
  { id: CharacterId; name: string; emoji: string; color: string }
> = {
  normal: { id: 'normal', name: 'Normal Person', emoji: '🧍', color: '#c4a882' },
  gamer: { id: 'gamer', name: 'Gamer', emoji: '🎮', color: '#5b8def' },
  nerd: { id: 'nerd', name: 'Nerd', emoji: '🤓', color: '#8fd16a' },
  goth: { id: 'goth', name: 'Goth', emoji: '🖤', color: '#6b5b95' },
  punk: { id: 'punk', name: 'Punk', emoji: '🎸', color: '#e85d75' },
  'old-guy': { id: 'old-guy', name: 'Old Guy', emoji: '🧓', color: '#d4b896' },
  alien: { id: 'alien', name: 'Alien', emoji: '👽', color: '#7dffa3' },
  robot: { id: 'robot', name: 'Robot', emoji: '🤖', color: '#9aa4b2' },
  skeleton: { id: 'skeleton', name: 'Skeleton', emoji: '💀', color: '#e8e4d9' },
  gopher: { id: 'gopher', name: 'Gopher', emoji: '🦫', color: '#c68642' },
  cat: { id: 'cat', name: 'Cat', emoji: '🐱', color: '#f0a35e' },
  'hot-dog': { id: 'hot-dog', name: 'Hot Dog', emoji: '🌭', color: '#e4572e' },
};

export const THROWABLE_IDS = [
  'tomato',
  'carrot',
  'broccoli',
  'potato',
  'egg',
  'popcorn',
  'shoe',
  'prop-knife',
  'pizza',
  'toilet-paper',
  'rubber-chicken',
  'paper-airplane',
  'beach-ball',
] as const;

export type ThrowableId = (typeof THROWABLE_IDS)[number];

export type FlightStyle =
  | 'arc-fast'
  | 'arc-heavy'
  | 'spin'
  | 'flail'
  | 'glide'
  | 'frisbee'
  | 'slow-bounce'
  | 'trail-spin';

export type ImpactStyle =
  | 'splat'
  | 'thunk'
  | 'bounce'
  | 'explode'
  | 'stick'
  | 'glide-land'
  | 'slide'
  | 'trail-splat';

export interface ThrowableDef {
  id: ThrowableId;
  name: string;
  emoji: string;
  flight: FlightStyle;
  impact: ImpactStyle;
  durationMs: number;
  projectileScale?: number;
}

/** Data-driven throwable registry. Add new items here. */
export const THROWABLES: Record<ThrowableId, ThrowableDef> = {
  tomato: {
    id: 'tomato',
    name: 'Tomato',
    emoji: '🍅',
    flight: 'arc-fast',
    impact: 'splat',
    durationMs: 3000,
  },
  carrot: {
    id: 'carrot',
    name: 'Carrot',
    emoji: '🥕',
    flight: 'arc-fast',
    impact: 'bounce',
    durationMs: 2800,
  },
  broccoli: {
    id: 'broccoli',
    name: 'Broccoli',
    emoji: '🥦',
    flight: 'arc-heavy',
    impact: 'bounce',
    durationMs: 3000,
  },
  potato: {
    id: 'potato',
    name: 'Potato',
    emoji: '🥔',
    flight: 'arc-heavy',
    impact: 'thunk',
    durationMs: 3200,
  },
  egg: {
    id: 'egg',
    name: 'Egg',
    emoji: '🥚',
    flight: 'arc-fast',
    impact: 'splat',
    durationMs: 3000,
  },
  popcorn: {
    id: 'popcorn',
    name: 'Popcorn',
    emoji: '🍿',
    flight: 'arc-fast',
    impact: 'explode',
    durationMs: 2800,
  },
  shoe: {
    id: 'shoe',
    name: 'Shoe',
    emoji: '👟',
    flight: 'spin',
    impact: 'bounce',
    durationMs: 3500,
  },
  'prop-knife': {
    id: 'prop-knife',
    name: 'Prop Knife',
    emoji: '🔪',
    flight: 'spin',
    impact: 'stick',
    durationMs: 3200,
  },
  pizza: {
    id: 'pizza',
    name: 'Pizza',
    emoji: '🍕',
    flight: 'frisbee',
    impact: 'slide',
    durationMs: 3200,
  },
  'toilet-paper': {
    id: 'toilet-paper',
    name: 'Toilet Paper',
    emoji: '🧻',
    flight: 'trail-spin',
    impact: 'trail-splat',
    durationMs: 3800,
  },
  'rubber-chicken': {
    id: 'rubber-chicken',
    name: 'Rubber Chicken',
    emoji: '🐔',
    flight: 'flail',
    impact: 'bounce',
    durationMs: 3600,
  },
  'paper-airplane': {
    id: 'paper-airplane',
    name: 'Paper Airplane',
    emoji: '✈️',
    flight: 'glide',
    impact: 'glide-land',
    durationMs: 4000,
  },
  'beach-ball': {
    id: 'beach-ball',
    name: 'Beach Ball',
    emoji: '🏖️',
    flight: 'slow-bounce',
    impact: 'bounce',
    durationMs: 4500,
    projectileScale: 1.4,
  },
};

export const REACTION_IDS = [
  'laugh',
  'clap',
  'shock',
  'love',
  'boo',
  'rock',
  'dead',
] as const;

export type ReactionId = (typeof REACTION_IDS)[number];

export const REACTION_META: Record<ReactionId, { emoji: string; name: string }> = {
  laugh: { emoji: '😂', name: 'Laugh' },
  clap: { emoji: '👏', name: 'Clap' },
  shock: { emoji: '😱', name: 'Shocked' },
  love: { emoji: '❤️', name: 'Love' },
  boo: { emoji: '👎', name: 'Boo' },
  rock: { emoji: '🤘', name: 'Rock' },
  dead: { emoji: '💀', name: 'Dead' },
};

export const THEATER_ROWS = 3;
export const THEATER_COLS = 7;
/** Seats per row: back, middle, front */
export const ROW_SEAT_COUNTS = [5, 6, 7] as const;

/** Horizontal padding % by row. Back rows are narrower, front rows are wider. */
export const ROW_PAD = [20, 10, 2] as const;
/** Vertical seat anchor % within seating area (MST3K rows rising toward screen) */
export const ROW_Y = [20, 35, 58] as const;

export interface SeatId {
  row: number;
  col: number;
}

export function seatKey(seat: SeatId): string {
  return `${seat.row}:${seat.col}`;
}

export function seatPercent(seat: SeatId): { x: number; y: number } {
  const seatsInRow = ROW_SEAT_COUNTS[seat.row] ?? THEATER_COLS;
  const pad = ROW_PAD[seat.row] ?? 8;
  const usable = 100 - pad * 2;
  const x = pad + ((seat.col + 0.5) / seatsInRow) * usable;
  const y = ROW_Y[seat.row] ?? 55;
  return { x, y };
}
